import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClientAuth } from '@/hooks/useClientAuth';
import { useAuth } from '@/hooks/useAuth';
import { CosmosWaveLayout } from '@/components/dashboard/CosmosWaveLayout';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { CredentialsForm } from '@/components/dashboard/CredentialsForm';
import { Loader2, ArrowLeft, Settings, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SEOHead } from '@/components/seo/SEOHead';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

export default function ClientSettings() {
  const { clientSlug } = useParams<{ clientSlug: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { currentClient, loading: clientLoading } = useClientAuth();
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [spApiConnected, setSpApiConnected] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);

  useEffect(() => {
    const checkSpApiConnection = async () => {
      if (!currentClient?.id) return;
      
      setCheckingConnection(true);
      try {
        const { data, error } = await supabase
          .from('api_credentials')
          .select('encrypted_secrets')
          .eq('client_id', currentClient.id)
          .eq('provider', 'amazon_sp')
          .maybeSingle();

        if (!error && data?.encrypted_secrets) {
          const secrets = data.encrypted_secrets as any;
          // Check if refresh_token exists and is not empty
          const hasRefreshToken = secrets.refresh_token && secrets.refresh_token.length > 0;
          setSpApiConnected(hasRefreshToken);
        }
      } catch (err) {
        console.error('[ClientSettings] Error checking connection:', err);
      } finally {
        setCheckingConnection(false);
      }
    };

    checkSpApiConnection();
  }, [currentClient, saveSuccess]);

  if (authLoading || clientLoading) {
    return (
      <CosmosWaveLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF8A00]" />
        </div>
      </CosmosWaveLayout>
    );
  }

  if (!user || !currentClient || currentClient.slug !== clientSlug) {
    return (
      <CosmosWaveLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2 text-[#E6EAED]">Accès refusé</h1>
            <p className="text-[#E6EAED]/70 mb-4">
              Vous n'avez pas accès à ces paramètres.
            </p>
            <Button
              onClick={() => navigate('/espace-client')}
              variant="outline"
            >
              Retour
            </Button>
          </div>
        </div>
      </CosmosWaveLayout>
    );
  }

  const handleSaveSuccess = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 5000);
  };

  return (
    <>
      <SEOHead 
        title={`Paramètres ${currentClient.name} - Configuration API`}
        description={`Configuration des credentials API Amazon pour ${currentClient.name}. SP-API, Amazon Ads, Seller Wallet.`}
      />
      
      <CosmosWaveLayout>
        <DashboardHeader client={currentClient} />
        
        <div className="container mx-auto p-6 space-y-6 max-w-5xl">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/dashboard/${clientSlug}`)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au dashboard
              </Button>
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#FF8A00]" />
                <h1 className="text-2xl font-bold text-[#E6EAED]">Paramètres API</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/fix-credentials')}
                title="Applique automatiquement Client ID, Secret et Refresh Token connus"
              >
                Corriger les credentials (auto)
              </Button>
            </div>
          </div>

          {saveSuccess && (
            <Alert className="border-green-500/50 bg-green-500/10">
              <AlertDescription className="text-green-400">
                ✅ Credentials sauvegardées avec succès ! Les données réelles seront disponibles dans quelques instants.
              </AlertDescription>
            </Alert>
          )}

          {/* Statut de connexion */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#E6EAED] mb-2">
                  Configuration des API Amazon
                </h2>
                <p className="text-[#E6EAED]/70 text-sm">
                  Configurez vos credentials API Amazon pour accéder aux données réelles de votre compte.
                  Toutes les informations sont chiffrées et stockées de manière sécurisée.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {checkingConnection ? (
                  <Loader2 className="w-4 h-4 animate-spin text-[#FF8A00]" />
                ) : spApiConnected ? (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    SP-API Connectée
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-[#E6EAED]/20 text-[#E6EAED]/70">
                    Non configurée
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Tabs pour chaque provider */}
          <Tabs defaultValue="amazon_sp" className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-[#1a1a1a]">
              <TabsTrigger value="amazon_sp">
                SP-API
              </TabsTrigger>
              <TabsTrigger value="amazon_ads">
                Amazon Ads
              </TabsTrigger>
              <TabsTrigger value="seller_wallet">
                Seller Wallet
              </TabsTrigger>
            </TabsList>

            <TabsContent value="amazon_sp" className="mt-6">
              <CredentialsForm 
                clientId={currentClient.id}
                provider="amazon_sp"
                onSaveSuccess={handleSaveSuccess}
              />
            </TabsContent>

            <TabsContent value="amazon_ads" className="mt-6">
              <CredentialsForm 
                clientId={currentClient.id}
                provider="amazon_ads"
                onSaveSuccess={handleSaveSuccess}
              />
            </TabsContent>

            <TabsContent value="seller_wallet" className="mt-6">
              <CredentialsForm 
                clientId={currentClient.id}
                provider="seller_wallet"
                onSaveSuccess={handleSaveSuccess}
              />
            </TabsContent>
          </Tabs>

          {/* Help section */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-6">
            <h3 className="text-sm font-semibold text-[#E6EAED] mb-3">
              📚 Besoin d'aide pour obtenir vos credentials ?
            </h3>
            <ul className="text-sm text-[#E6EAED]/70 space-y-2">
              <li>• <strong>SP-API</strong> : Accédez à Seller Central → Paramètres → Accès développeur</li>
              <li>• <strong>Amazon Ads</strong> : Console Advertising → Paramètres → Clés API</li>
              <li>• <strong>Seller Wallet</strong> : Utilisez les mêmes credentials que SP-API</li>
            </ul>
          </div>
        </div>
      </CosmosWaveLayout>
    </>
  );
}
